import i18n from '@/lib/i18n';
import { recordHandledError } from '@/lib/crashReporting';

type Row = Record<string, unknown>;
type SqlExecutor = (sql: string, params?: unknown[]) => Promise<unknown>;

/**
 * O pai ausente ainda pode chegar vivo por outro caminho (o histórico do sync o dá como vivo e
 * só não foi aplicado). Recriá-lo EXCLUÍDO aqui seria pior que a órfã: o INSERT do sync só
 * carrega coluna não nula, então o `deleted_at` do placeholder sobreviveria à linha real e o
 * cartão de verdade nasceria escondido. Pular deixa a referência pra próxima passada.
 */
export type ParentStillComing = (table: string, id: string) => Promise<boolean>;

export interface DanglingRepairReport {
    /** Contas recriadas como excluídas. */
    accounts: number;
    /** Cartões recriados como excluídos. */
    creditCards: number;
    /** Linhas cujo vínculo com fatura inexistente virou NULL. */
    invoiceLinks: number;
    /** Linhas cujo vínculo com subcategoria inexistente virou NULL (a categoria fica). */
    subcategoryLinks: number;
    /** Capturas de notificação cujo vínculo com lançamento apagado de vez virou NULL. */
    notificationLinks: number;
    /** Linhas que continuam apontando pro nada porque a aresta não tem conserto seguro. */
    unrepaired: number;
    /** Linhas puladas porque o pai ainda pode chegar vivo pelo sync (ver ParentStillComing). */
    pending: number;
    /** Onde ficou o `unrepaired`: `tabela.coluna` → linhas. Só nome de coluna, nunca id. */
    unrepairedEdges: Record<string, number>;
    /** Onde ficou o `pending`, no mesmo formato. */
    pendingEdges: Record<string, number>;
}

export type DanglingRepairSource = 'boot' | 'restore' | 'sync';

/**
 * v2: a v1 não tratava subcategoria, e fechou a chave nas bases que só tinham essa órfã
 * (Sentry REACT-NATIVE-32, `unrepaired`). A chave nova faz a passada rodar mais uma vez nelas.
 */
const DONE_KEY = 'dangling_refs_repair_v2';

function rowsOf(result: unknown): Row[] {
    return (result as { rows?: Row[] } | undefined)?.rows ?? [];
}

interface Edge {
    table: string;
    column: string;
    parent: string;
    parentColumn: string;
    nullable: boolean;
    /** A tabela filha tem `credit_card_id` (dá pra saber se a linha é de cartão excluído). */
    hasCardColumn: boolean;
    /** FK de várias colunas (o schema não tem; se aparecer, só conta). */
    composite: boolean;
}

/** Valores pendurados da aresta: a coluna aponta pra uma linha que não existe no pai. */
async function danglingValues(execute: SqlExecutor, edge: Edge): Promise<{ value: string; rows: number }[]> {
    const rows = rowsOf(await execute(
        `SELECT \`${edge.column}\` AS value, count(*) AS rows FROM \`${edge.table}\`
          WHERE \`${edge.column}\` IS NOT NULL
            AND \`${edge.column}\` NOT IN (SELECT \`${edge.parentColumn}\` FROM \`${edge.parent}\`)
          GROUP BY \`${edge.column}\``,
    ));
    return rows.map((r) => ({ value: String(r.value), rows: Number(r.rows) }));
}

/**
 * Referência pendurada: linha apontando pra conta, cartão ou fatura que NÃO EXISTE no banco
 * (nem excluída). Nasce de escrita com FK OFF: o restore de um backup que não levava o pai
 * excluído (antes da ADR 0165) e o apply do sync quando outro aparelho apagou o pai de vez.
 * O SQLite não reclama na hora. Estoura depois:
 * - na primeira escrita que regrava a coluna: editar o lançamento da conta excluída
 *   (Sentry REACT-NATIVE-7, "FOREIGN KEY constraint failed");
 * - quando a tela exige o pai: fatura do cartão excluído (REACT-NATIVE-2V, "Credit card not found");
 * - e em silêncio: a compra do cartão excluído volta pros relatórios, porque o filtro é "não
 *   existe cartão apagado" e cartão AUSENTE passa.
 *
 * O reparo devolve o banco ao estado do aparelho de origem:
 * - conta ou cartão ausente volta EXCLUÍDO, com o mesmo id e um nome genérico (o original se
 *   perdeu). Invisível como era; os lançamentos voltam a ser editáveis e somem das leituras
 *   que já escondiam cartão excluído;
 * - fatura ausente, em lançamento de cartão excluído: a coluna opcional que aponta pra ela vira
 *   NULL. Fatura não tem soft-delete e exige datas que se perderam; no caminho real só some
 *   fatura de cartão excluído, cujos lançamentos nenhuma leitura mostra;
 * - subcategoria ausente: a coluna opcional que aponta pra ela vira NULL, o mesmo que o
 *   `deleteSubcategory` faz. A classificação fica só na categoria. O "Limpar excluídos" de antes
 *   do 9b8c519 apagava a subcategoria sem desligar regra e parcelamento, e editar a regra
 *   estourava FK (REACT-NATIVE-32);
 * - lançamento ausente numa notificação capturada: o `created_transaction_id` vira NULL. O
 *   lançamento criado por ela foi apagado de vez e a captura só guardava o vínculo. Era todo o
 *   `unrepaired` do Sentry em out/2026;
 * - qualquer outra aresta só é CONTADA (`unrepaired`, com a quebra em `unrepairedEdges`), sem
 *   mexer. Vira sinal no Sentry.
 *
 * Idempotente e barato quando não há o que fazer (um `PRAGMA foreign_key_check`). O executor
 * vem por parâmetro pelo mesmo motivo do zombieInvoiceCleanup: importar o client daqui fecharia
 * ciclo no boot, e o teste injeta o SQLite real do dbHarness.
 */
export async function repairDanglingReferences(
    execute: SqlExecutor,
    options?: { parentStillComing?: ParentStillComing },
): Promise<DanglingRepairReport> {
    const report: DanglingRepairReport = {
        accounts: 0,
        creditCards: 0,
        invoiceLinks: 0,
        subcategoryLinks: 0,
        notificationLinks: 0,
        unrepaired: 0,
        pending: 0,
        unrepairedEdges: {},
        pendingEdges: {},
    };
    const leaveDangling = (edge: Edge, rows: number) => {
        if (rows <= 0) return;
        const key = `${edge.table}.${edge.column}`;
        report.unrepaired += rows;
        report.unrepairedEdges[key] = (report.unrepairedEdges[key] ?? 0) + rows;
    };
    const violations = rowsOf(await execute('PRAGMA foreign_key_check'));
    if (violations.length === 0) return report;

    const edges = new Map<string, Edge>();
    for (const v of violations) {
        const table = String(v.table);
        const key = `${table}#${String(v.fkid)}`;
        if (edges.has(key)) continue;
        const parts = rowsOf(await execute(`PRAGMA foreign_key_list(\`${table}\`)`))
            .filter((fk) => String(fk.id) === String(v.fkid));
        const column = String(parts[0]?.from ?? '');
        const tableInfo = rowsOf(await execute(`PRAGMA table_info(\`${table}\`)`));
        const info = tableInfo.find((c) => c.name === column);
        edges.set(key, {
            table,
            column,
            parent: String(v.parent),
            parentColumn: String(parts[0]?.to ?? 'id'),
            nullable: Number(info?.notnull ?? 1) === 0,
            hasCardColumn: tableInfo.some((c) => c.name === 'credit_card_id'),
            composite: parts.length !== 1,
        });
    }

    const now = new Date().toISOString();
    const stillComing = options?.parentStillComing;
    // Duas arestas pro mesmo pai ausente (despesa e pagamento no mesmo cartão) contam um pai só.
    const recreated = { accounts: new Set<string>(), credit_cards: new Set<string>() };
    // Pais primeiro: o cartão recriado também resolve a fatura que apontava pra ele.
    const order = (e: Edge) => (e.parent === 'invoices' ? 1 : 0);
    for (const edge of [...edges.values()].sort((a, b) => order(a) - order(b))) {
        if (edge.composite) {
            leaveDangling(edge, violations.filter((v) => v.table === edge.table && v.parent === edge.parent).length);
            continue;
        }
        for (const { value, rows } of await danglingValues(execute, edge)) {
            const isNotificationLink = edge.table === 'pending_notifications' && edge.parent === 'transactions';
            const repairable = edge.parent === 'accounts'
                || edge.parent === 'credit_cards'
                || ((edge.parent === 'invoices' || edge.parent === 'subcategories' || isNotificationLink) && edge.nullable);
            if (!repairable) {
                leaveDangling(edge, rows);
                continue;
            }
            if (stillComing && await stillComing(edge.parent, value)) {
                const key = `${edge.table}.${edge.column}`;
                report.pending += rows;
                report.pendingEdges[key] = (report.pendingEdges[key] ?? 0) + rows;
                continue;
            }
            if (edge.parent === 'accounts') {
                if (recreated.accounts.has(value)) continue;
                recreated.accounts.add(value);
                await execute(
                    `INSERT OR IGNORE INTO accounts (id, name, type, created_at, updated_at, deleted_at)
                     VALUES (?, ?, 'checking', ?, ?, ?)`,
                    [value, i18n.t('ui:deletedRecords.account'), now, now, now],
                );
            } else if (edge.parent === 'credit_cards') {
                if (recreated.credit_cards.has(value)) continue;
                recreated.credit_cards.add(value);
                // Fechamento/vencimento quaisquer: a fatura de um cartão excluído não aparece em
                // lugar nenhum, e os dias originais se perderam junto com a linha.
                await execute(
                    `INSERT OR IGNORE INTO credit_cards (id, name, closing_day, due_day, created_at, updated_at, deleted_at)
                     VALUES (?, ?, 1, 10, ?, ?, ?)`,
                    [value, i18n.t('ui:deletedRecords.card'), now, now, now],
                );
            } else if (edge.parent === 'subcategories') {
                await execute(`UPDATE \`${edge.table}\` SET \`${edge.column}\` = NULL WHERE \`${edge.column}\` = ?`, [value]);
                report.subcategoryLinks += rows;
            } else if (isNotificationLink) {
                await execute(`UPDATE \`${edge.table}\` SET \`${edge.column}\` = NULL WHERE \`${edge.column}\` = ?`, [value]);
                report.notificationLinks += rows;
            } else {
                // Só solta a fatura de lançamento de cartão EXCLUÍDO (o único caminho conhecido:
                // o backup não levava a fatura do cartão excluído). Num cartão vivo, anular tiraria
                // a compra da fatura em silêncio; ali ela fica pendurada e só vira sinal.
                const scoped = edge.hasCardColumn
                    ? ` AND (credit_card_id IS NULL OR credit_card_id IN (SELECT id FROM credit_cards WHERE deleted_at IS NOT NULL))`
                    : '';
                const [scopedRows] = rowsOf(await execute(
                    `SELECT count(*) AS n FROM \`${edge.table}\` WHERE \`${edge.column}\` = ?${scoped}`,
                    [value],
                ));
                const released = Number(scopedRows?.n ?? 0);
                await execute(
                    `UPDATE \`${edge.table}\` SET \`${edge.column}\` = NULL WHERE \`${edge.column}\` = ?${scoped}`,
                    [value],
                );
                report.invoiceLinks += released;
                leaveDangling(edge, rows - released);
            }
        }
    }
    report.accounts = recreated.accounts.size;
    report.creditCards = recreated.credit_cards.size;
    return report;
}

/**
 * Passada única no boot, pra base que já quebrou antes deste reparo existir (os usuários do
 * RN-7 e do RN-2V). A chave mora em `settings` e viaja no backup; tudo bem, porque o restore
 * repara sozinho (`applyBackupContent`) e não depende dela.
 */
export async function repairDanglingReferencesOnce(
    execute: SqlExecutor,
    options?: { parentStillComing?: ParentStillComing },
): Promise<DanglingRepairReport | null> {
    const guard = rowsOf(await execute('SELECT value FROM settings WHERE key = ?', [DONE_KEY]));
    if (guard.length > 0) return null;
    const report = await repairDanglingReferences(execute, options);
    // Pai ainda a caminho pelo sync: não fecha a chave, pra tentar de novo no próximo boot.
    // Aresta sem conserto (`unrepaired`) fecha: repetir a cada boot não mudaria nada.
    if (report.pending === 0) {
        await execute(
            `INSERT OR REPLACE INTO settings (key, value, updated_at) VALUES (?, '1', ?)`,
            [DONE_KEY, new Date().toISOString()],
        );
    }
    return report;
}

/** Tag do Sentry aceita até 200 caracteres. */
const EDGES_TAG_MAX = 200;

/**
 * `unrepairedEdges` como tag: maior primeiro, `tabela.coluna=linhas`. O que não couber vira
 * `+N` no fim (N arestas a mais), pra a tag nunca ser cortada no meio de um nome.
 */
export function describeUnrepairedEdges(edges: Record<string, number>): string {
    const parts = Object.entries(edges)
        .sort(([a, x], [b, y]) => y - x || a.localeCompare(b))
        .map(([edge, rows]) => `${edge}=${rows}`);
    const kept: string[] = [];
    for (const part of parts) {
        const left = parts.length - kept.length - 1;
        const candidate = [...kept, part, ...(left > 0 ? [`+${left}`] : [])].join(',');
        if (candidate.length > EDGES_TAG_MAX) break;
        kept.push(part);
    }
    const dropped = parts.length - kept.length;
    return [...kept, ...(dropped > 0 ? [`+${dropped}`] : [])].join(',');
}

/** Quantas bases carregam referência pendurada, e de qual tipo. Só contagem, nunca id. */
export function reportDanglingRepair(report: DanglingRepairReport | null, source: DanglingRepairSource): void {
    if (!report) return;
    const touched = report.accounts + report.creditCards + report.invoiceLinks + report.subcategoryLinks
        + report.notificationLinks + report.unrepaired + report.pending;
    if (touched === 0) return;
    recordHandledError(new Error('Referência pendurada reparada'), {
        kontto_op: 'dangling_refs_repaired',
        source,
        accounts: report.accounts,
        credit_cards: report.creditCards,
        invoice_links: report.invoiceLinks,
        subcategory_links: report.subcategoryLinks,
        notification_links: report.notificationLinks,
        unrepaired: report.unrepaired,
        pending: report.pending,
        ...(report.unrepaired > 0 ? { unrepaired_edges: describeUnrepairedEdges(report.unrepairedEdges) } : {}),
        ...(report.pending > 0 ? { pending_edges: describeUnrepairedEdges(report.pendingEdges) } : {}),
    });
}
